/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { Modal } from "react-overlays";
import { useMediaQuery } from "react-responsive";
import { css } from "@emotion/react";
import {
  Button,
  Column,
  ContextMenu,
  Row,
  Text,
  TEXT_VARIANTS,
  Icon,
  IconNames,
  GlobalSearch,
  Feed,
  ListItem,
  Scrollable,
  Sidebar,
  SidebarSection,
  SidebarSectionListItem,
  Flex,
  SidebarSectionContext,
  Tagline,
  Card,
} from "../components";
import { RootContext } from "./Root";
import { COLORS } from "../shared/theme";
import styled from "@emotion/styled";

const SORT_CRITERIA = {
  MOST_RECENT: "Most recent",
  MOST_CONNECTED: "Most connected",
  RANDOM: "Random",
} as const;

const ENTITY_TYPES = {
  EVERYTHING: "Everything",
  ARTICLES: "Articles",
  AUDIO: "Audio",
  BOOKS: "Books",
  HIGHLIGHTS: "Highlights",
  PICTURES: "Pictures",
  TEXT: "Texts",
  TWEETS: "Tweets",
  VIDEO: "Videos",
  WEBSITES: "Websites",
} as const;

const ICON_MAP = {
  EVERYTHING: IconNames.Everything,
  ARTICLES: IconNames.Articles,
  AUDIO: IconNames.Listen,
  BOOKS: IconNames.Books,
  HIGHLIGHTS: IconNames.Highlights,
  PICTURES: IconNames.Pictures,
  TEXT: IconNames.Texts,
  TWEETS: IconNames.Tweets,
  VIDEO: IconNames.Videos,
  WEBSITES: IconNames.Websites,
} as const;

type TProfileContext = {
  isCollapsibleHeaderExpanded: boolean;
  selectedEntityTypes: Array<string>;
  setSelectedEntityTypes: React.Dispatch<React.SetStateAction<Array<string>>>;
  toggleEntityType: (key: string) => void;
  sortCriteria: typeof SORT_CRITERIA[keyof typeof SORT_CRITERIA];
  setSortCriteria: React.Dispatch<
    React.SetStateAction<typeof SORT_CRITERIA[keyof typeof SORT_CRITERIA]>
  >;
};

const ProfileContext = React.createContext<TProfileContext>({
  isCollapsibleHeaderExpanded: false,
  selectedEntityTypes: ["Everything"],
  setSelectedEntityTypes: () => {},
  toggleEntityType: () => {},
  sortCriteria: SORT_CRITERIA.MOST_RECENT,
  setSortCriteria: () => {},
});

const ProfileCollapsibleHeader = () => {
  const { isCollapsibleHeaderExpanded } = useContext(ProfileContext);
  const isMobile = useMediaQuery({ maxWidth: 700 });

  return (
    <Row
      mainAxisAlignment="center"
      css={css`
        transition: all 120ms ease;
        transform: translateY(${isCollapsibleHeaderExpanded ? 55 : -55}px);
        position: fixed;
        top: 0;
        z-index: 1;
        height: 55px;
        width: 100%;
        background-color: ${COLORS.WHITE};
        box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.1);
      `}
    >
      <Row
        mainAxisAlignment="space-between"
        crossAxisAlignment="center"
        css={css`
          width: 1266px;
          max-width: calc(100% - 20px);
        `}
      >
        <Row mainAxisAlignment="center" crossAxisAlignment="center" gap={5}>
          <Button leadingElement={() => <Icon name={IconNames.Back} />} />
          <Text variant={TEXT_VARIANTS.T20BOLD}>
            {
              "Building bicycles for our minds. Technology has a hole in it, I can feel… | by John Borthwick | Render-fro..."
            }
          </Text>
        </Row>
        <Row gap={5} mainAxisAlignment="center" crossAxisAlignment="center">
          {!isMobile && (
            <Button leadingElement={() => <Icon name={IconNames.More} />} />
          )}
          <Button leadingElement={() => <Icon name={IconNames.Share} />} />
          <Button
            variant="solid"
            label="Connect"
            textVariant={TEXT_VARIANTS.T20SEMIBOLD}
            trailingElement={() => <Icon name={IconNames.Connect} />}
          />
        </Row>
      </Row>
    </Row>
  );
};

const Drawer = styled(Modal)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100vw;
  height: 100%;
  background-color: ${COLORS.WHITE};
`;

const Note: React.FC = () => {
  const { isNavigationExpanded, sidebarGap } = useContext(RootContext);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([
    "EVERYTHING",
  ]);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [didSelectedFavorites, setDidSelectFavorites] = useState(false);
  const [sortCriteria, setSortCriteria] = useState<
    typeof SORT_CRITERIA[keyof typeof SORT_CRITERIA]
  >(SORT_CRITERIA.MOST_RECENT);
  const isMobile = useMediaQuery({ maxWidth: 700 });
  const [isCollapsibleHeaderExpanded, setIsCollapsibleHeaderExpanded] =
    useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarSelection, setSidebarSelection] = useState<
    Array<[string, Array<string>]>
  >([]);

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 272) setIsCollapsibleHeaderExpanded(true);
      else setIsCollapsibleHeaderExpanded(false);
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleEntityType = useCallback(
    (key: string) => {
      const entitiesIndex = sidebarSelection.findIndex(
        (section) => section[0] === "entities"
      );
      console.log(entitiesIndex);
      if (entitiesIndex > -1) {
        //const newSelection = [['entities', []]] as Array<[string, Array<string>]>;
        const newSelection = sidebarSelection.slice();
        const itemIndex = newSelection[entitiesIndex][1].findIndex(
          (entity) => key === entity
        );
        if (itemIndex > -1)
          newSelection[entitiesIndex][1] = newSelection[
            entitiesIndex
          ][1].filter((entity) => entity !== key);
        else newSelection[entitiesIndex][1].push(key);

        setSidebarSelection(newSelection);
      }
      setSelectedEntityTypes((selectedEntityTypes) => {
        if (selectedEntityTypes.includes(key))
          if (selectedEntityTypes.length === 1) return ["EVERYTHING"];
          else return selectedEntityTypes.filter((type) => type !== key);
        else
          return selectedEntityTypes
            .filter((type) => type !== "EVERYTHING")
            .concat([key]);
      });
    },
    [setSelectedEntityTypes, sidebarSelection]
  );

  return (
    <ProfileContext.Provider
      value={{
        isCollapsibleHeaderExpanded,
        selectedEntityTypes,
        setSelectedEntityTypes,
        toggleEntityType,
        sortCriteria,
        setSortCriteria,
      }}
    >
      <ProfileCollapsibleHeader />
      <Column
        gap={isMobile ? 25 : 50}
        crossAxisAlignment="center"
        css={css`
          width: 100%;
        `}
      >
        <Row
          mainAxisAlignment="center"
          css={css`
            width: 766px;
            max-width: calc(100% - 20px);
          `}
        >
          <Card
            css={css`
              transition: all 120ms ease;
              cursor: pointer;
              padding: 10px;
              border: none;
              box-shadow: 0 0.1rem 0.5rem rgb(0 0 0 / 10%);

              &:hover {
                box-shadow: 0 0.5rem 1.5rem rgb(0 0 0 / 10%);
              }

              &:hover .feed-card__footer__connections {
                color: ${COLORS.GREEN};
              }
            `}
          >
            <Column gap={10}>
              <Text variant={TEXT_VARIANTS.T20}>
                {
                  "If you’re struggling to get dazzling work out of AI engines, PromptBase is a marketplace to find top prompts and sell your own prompts (curated in jobs of the future and weird internet careers - because prompt engineering is quickly becoming a viable career path)."
                }
              </Text>
              <Column crossAxisAlignment="start" gap={5}>
                <Tagline
                  text="by $0 from $1"
                  entities={[
                    { type: "member", name: "Sari Azout" },
                    { type: "article", name: "PromptBase" },
                  ]}
                />
                <Text
                  variant={TEXT_VARIANTS.T20}
                  css={css`
                    color: ${COLORS.SILVER};
                  `}
                >
                  {"note · updated 3d ago"}
                </Text>
                <Tagline
                  text="curated by $0(1st) and $1"
                  entities={[
                    { type: "user", name: "Steve Jobs" },
                    { type: "user", name: "33 others" },
                  ]}
                />
              </Column>
            </Column>
            <Row crossAxisAlignment="center" mainAxisAlignment="end">
              <Row gap={5}>
                <ContextMenu
                  renderElement={(toggleOpen) => (
                    <Button
                      leadingElement={() => <Icon name={IconNames.More} />}
                      onClick={toggleOpen}
                    />
                  )}
                  renderMenu={() => (
                    <Column
                      gap={5}
                      css={css`
                        width: 250px;
                        padding: 5px;
                      `}
                    >
                      <ListItem
                        icon={<Icon name={IconNames.Edit} />}
                        label="Edit"
                      />
                      <ListItem
                        icon={<Icon name={IconNames.Share} />}
                        label="Copy link"
                      />
                      <ListItem
                        icon={<Icon name={IconNames.Pinned} />}
                        label="Pin"
                      />
                      <ListItem
                        icon={<Icon name={IconNames.Unpin} />}
                        label="Unnpin"
                      />
                      <ListItem
                        icon={<Icon name={IconNames.Favorites} />}
                        label="Favorite"
                      />
                      <ListItem
                        icon={<Icon name={IconNames.Unfavorite} />}
                        label="Unfavorite"
                      />
                      <ListItem
                        icon={<Icon name={IconNames.Disconnect} />}
                        label="Remove connection"
                      />
                      <ListItem
                        icon={<Icon name={IconNames.Delete} />}
                        label="Delete"
                      />
                    </Column>
                  )}
                />
                <Button
                  leadingElement={() => <Icon name={IconNames.Share} />}
                />
                <Button
                  label="Connect"
                  trailingElement={() => <Icon name={IconNames.Connect} />}
                />
              </Row>
            </Row>
          </Card>
        </Row>
        <Flex
          direction={isMobile ? "column" : "row"}
          gap={isMobile ? 25 : 100}
          css={css`
            width: 1266px;
            max-width: calc(100% - ${isMobile ? 20 : 100}px);
          `}
        >
          {isMobile ? (
            <Column gap={5}>
              <GlobalSearch
                value=""
                onChange={() => {}}
                onEnter={() => {}}
                onEsc={() => {}}
              />
              <Row
                role="button"
                mainAxisAlignment="space-between"
                crossAxisAlignment="center"
                css={css`
                  height: 35px;
                  padding: 0 5px;
                  border-radius: 10px;
                  background-color: ${COLORS.LIGHT};
                `}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                {selectedEntityTypes.filter((entity) => entity !== "EVERYTHING")
                  .length +
                  (didSelectedFavorites ? 1 : 0) +
                  (selectedCollection.length > 0 ? 1 : 0) ===
                0 ? (
                  <Row gap={5} crossAxisAlignment="center">
                    <Icon name={ICON_MAP.EVERYTHING} />
                    <Text variant={TEXT_VARIANTS.T20}>{"Everything"}</Text>
                  </Row>
                ) : selectedEntityTypes.filter(
                    (entity) => entity !== "EVERYTHING"
                  ).length +
                    (didSelectedFavorites ? 1 : 0) +
                    (selectedCollection.length > 0 ? 1 : 0) ===
                  1 ? (
                  <Row gap={5} crossAxisAlignment="center">
                    <Icon
                      name={
                        selectedEntityTypes.filter(
                          (entity) => entity !== "EVERYTHING"
                        ).length === 1
                          ? ICON_MAP[
                              selectedEntityTypes.filter(
                                (entity) => entity !== "EVERYTHING"
                              )[0] as keyof typeof ENTITY_TYPES
                            ]
                          : didSelectedFavorites
                          ? IconNames.Favorites
                          : IconNames.Collection
                      }
                    />
                    <Text variant={TEXT_VARIANTS.T20}>
                      {selectedEntityTypes.filter(
                        (entity) => entity !== "EVERYTHING"
                      ).length === 1
                        ? ENTITY_TYPES[
                            selectedEntityTypes.filter(
                              (entity) => entity !== "EVERYTHING"
                            )[0] as keyof typeof ENTITY_TYPES
                          ]
                        : didSelectedFavorites
                        ? "Favorites"
                        : selectedCollection}
                    </Text>
                  </Row>
                ) : (
                  <Row gap={5} crossAxisAlignment="center">
                    <Icon name={IconNames.Filters} />
                    <Text variant={TEXT_VARIANTS.T20}>{"Filters "}</Text>
                    <Text
                      variant={TEXT_VARIANTS.T20}
                      css={css`
                        color: ${COLORS.SILVER};
                      `}
                    >
                      {`(${
                        selectedEntityTypes.filter(
                          (entity) => entity !== "EVERYTHING"
                        ).length +
                        (didSelectedFavorites ? 1 : 0) +
                        (selectedCollection.length > 0 ? 1 : 0)
                      })`}
                    </Text>
                  </Row>
                )}
                <Icon
                  name={IconNames.Expand}
                  css={css`
                    fill: ${COLORS.SILVER};
                  `}
                />
              </Row>
              {selectedCollection.length > 0 && (
                <Row
                  crossAxisAlignment="center"
                  gap={5}
                  css={css`
                    height: 35px;
                  `}
                >
                  <Icon name={IconNames.Jump} />
                  <Text
                    variant={TEXT_VARIANTS.T20SEMIBOLD}
                    css={css`
                      color: ${COLORS.SILVER};
                    `}
                  >
                    {"Jump to collection"}
                  </Text>
                </Row>
              )}
              <Drawer show={isSidebarOpen}>
                <>
                  <Scrollable height="100vh">
                    <div
                      css={css`
                        min-height: 100vh;
                        padding: 25px 5px 150px;
                      `}
                    >
                      <Sidebar
                        value={sidebarSelection}
                        onChange={(value) => {
                          setSidebarSelection(value);
                          const entitiesIndex = value.findIndex(
                            (section) => section[0] === "entities"
                          );
                          if (entitiesIndex > -1)
                            setSelectedEntityTypes(value[entitiesIndex][1]);
                          setDidSelectFavorites(
                            value.findIndex(
                              (section) => section[0] === "favorites"
                            ) > -1 &&
                              value[
                                value.findIndex(
                                  (section) => section[0] === "favorites"
                                )
                              ][1].length > 0
                          );
                          setSelectedCollection(
                            (value.findIndex(
                              (section) => section[0] === "collections"
                            ) > -1 &&
                              value[
                                value.findIndex(
                                  (section) => section[0] === "collections"
                                )
                              ][1] &&
                              value[
                                value.findIndex(
                                  (section) => section[0] === "collections"
                                )
                              ][1][0]) ||
                              ""
                          );
                        }}
                      >
                        <SidebarSection id="entities" multi>
                          <SidebarSectionContext.Consumer>
                            {({ selected, updateSelected }) => {
                              if (
                                selected.includes("EVERYTHING") &&
                                selected.length > 1
                              )
                                updateSelected(
                                  selected.filter((id) => id !== "EVERYTHING")
                                );
                              if (selected.length === 0)
                                updateSelected(["EVERYTHING"]);

                              return (
                                <>
                                  <SidebarSectionListItem
                                    id="EVERYTHING"
                                    icon={<Icon name={ICON_MAP.EVERYTHING} />}
                                    count={888}
                                    label={ENTITY_TYPES.EVERYTHING}
                                    onClick={(isActive) => {
                                      if (!isActive)
                                        updateSelected(["EVERYTHING"]);
                                    }}
                                  />
                                </>
                              );
                            }}
                          </SidebarSectionContext.Consumer>
                          {Object.keys(ENTITY_TYPES)
                            .filter((entityType) => entityType !== "EVERYTHING")
                            .map((entityType) => (
                              <SidebarSectionListItem
                                id={entityType}
                                icon={
                                  <Icon
                                    name={
                                      ICON_MAP[
                                        entityType as keyof typeof ICON_MAP
                                      ]
                                    }
                                  />
                                }
                                count={888}
                                label={
                                  ENTITY_TYPES[
                                    entityType as keyof typeof ENTITY_TYPES
                                  ]
                                }
                              />
                            ))}
                        </SidebarSection>
                      </Sidebar>
                    </div>
                  </Scrollable>
                  <Row
                    gap={10}
                    css={css`
                      position: absolute;
                      bottom: 0;
                      width: 100%;
                      padding: 10px 10px 25px;
                      background-color: #fff;
                    `}
                  >
                    <Button
                      variant="caps"
                      label="Clear"
                      textColor={COLORS.SILVER}
                      size="big"
                      css={css`
                        flex-grow: 1;
                      `}
                      onClick={() => setSidebarSelection([])}
                    />
                    <Button
                      variant="caps-active"
                      label="Done"
                      size="big"
                      css={css`
                        flex-grow: 1;
                      `}
                      onClick={() => setIsSidebarOpen(false)}
                    />
                  </Row>
                </>
              </Drawer>
            </Column>
          ) : (
            <div
              css={css`
                width: 40%;
              `}
            >
              <div
                css={css`
                  transition: all 120ms ease;
                  position: sticky;
                  top: ${isNavigationExpanded
                    ? 55 + sidebarGap + (isCollapsibleHeaderExpanded ? 55 : 0)
                    : sidebarGap}px;
                  height: fit-content;
                `}
              >
                <Scrollable height="calc(100vh - 125px)">
                  <Sidebar
                    value={sidebarSelection}
                    onChange={(value) => {
                      setSidebarSelection(value);
                      const entitiesIndex = value.findIndex(
                        (section) => section[0] === "entities"
                      );
                      if (entitiesIndex > -1)
                        setSelectedEntityTypes(value[entitiesIndex][1]);
                      setDidSelectFavorites(
                        value.findIndex(
                          (section) => section[0] === "favorites"
                        ) > -1 &&
                          value[
                            value.findIndex(
                              (section) => section[0] === "favorites"
                            )
                          ][1].length > 0
                      );
                      setSelectedCollection(
                        (value.findIndex(
                          (section) => section[0] === "collections"
                        ) > -1 &&
                          value[
                            value.findIndex(
                              (section) => section[0] === "collections"
                            )
                          ][1] &&
                          value[
                            value.findIndex(
                              (section) => section[0] === "collections"
                            )
                          ][1][0]) ||
                          ""
                      );
                    }}
                  >
                    <GlobalSearch
                      value=""
                      onChange={() => {}}
                      onEnter={() => {}}
                      onEsc={() => {}}
                    />
                    <SidebarSection id="entities" multi>
                      <SidebarSectionContext.Consumer>
                        {({ selected, updateSelected }) => {
                          if (
                            selected.includes("EVERYTHING") &&
                            selected.length > 1
                          )
                            updateSelected(
                              selected.filter((id) => id !== "EVERYTHING")
                            );
                          if (selected.length === 0)
                            updateSelected(["EVERYTHING"]);

                          return (
                            <>
                              <SidebarSectionListItem
                                id="EVERYTHING"
                                icon={<Icon name={ICON_MAP.EVERYTHING} />}
                                count={888}
                                label={ENTITY_TYPES.EVERYTHING}
                                onClick={(isActive) => {
                                  if (!isActive) updateSelected(["EVERYTHING"]);
                                }}
                              />
                            </>
                          );
                        }}
                      </SidebarSectionContext.Consumer>
                      {Object.keys(ENTITY_TYPES)
                        .filter((entityType) => entityType !== "EVERYTHING")
                        .map((entityType) => (
                          <SidebarSectionListItem
                            id={entityType}
                            icon={
                              <Icon
                                name={
                                  ICON_MAP[entityType as keyof typeof ICON_MAP]
                                }
                              />
                            }
                            count={888}
                            label={
                              ENTITY_TYPES[
                                entityType as keyof typeof ENTITY_TYPES
                              ]
                            }
                          />
                        ))}
                    </SidebarSection>
                  </Sidebar>
                </Scrollable>
              </div>
            </div>
          )}
          <Column
            as="main"
            gap={25}
            css={css`
              width: ${isMobile ? 100 : 60}%;
            `}
          >
            {!isMobile && (
              <Row mainAxisAlignment="space-between">
                <Row
                  css={css`
                    width: calc(100% - 200px);
                    flex-wrap: wrap;
                    row-gap: 10px;
                    column-gap: 5px;
                  `}
                >
                  {selectedEntityTypes
                    .filter((entityType) => entityType !== "EVERYTHING")
                    .map((entityType) => (
                      <Button
                        variant="solid"
                        label={
                          ENTITY_TYPES[entityType as keyof typeof ENTITY_TYPES]
                        }
                        key={entityType}
                        textVariant={TEXT_VARIANTS.T20}
                        textColor={COLORS.BLACK}
                        leadingElement={() => (
                          <Icon
                            name={
                              ICON_MAP[
                                entityType.toUpperCase() as keyof typeof ICON_MAP
                              ]
                            }
                          />
                        )}
                        trailingElement={() => <Icon name={IconNames.X} />}
                        onClick={() => toggleEntityType(entityType)}
                      />
                    ))}
                  {didSelectedFavorites && (
                    <Button
                      variant="solid"
                      label="Favorites"
                      textVariant={TEXT_VARIANTS.T20}
                      textColor={COLORS.BLACK}
                      leadingElement={() => <Icon name={IconNames.Favorites} />}
                      trailingElement={() => <Icon name={IconNames.X} />}
                      onClick={() => setDidSelectFavorites(false)}
                    />
                  )}
                  {selectedCollection !== "" && (
                    <Button
                      variant="solid"
                      label={selectedCollection}
                      textVariant={TEXT_VARIANTS.T20}
                      textColor={COLORS.BLACK}
                      leadingElement={() => (
                        <Icon name={IconNames.Collection} />
                      )}
                      trailingElement={() => <Icon name={IconNames.X} />}
                      onClick={() => setSelectedCollection("")}
                    />
                  )}
                </Row>
                <ContextMenu
                  renderElement={(toggleOpen) => (
                    <Button
                      label={sortCriteria}
                      trailingElement={() => (
                        <Icon name={IconNames.Sort} size={25} />
                      )}
                      onClick={toggleOpen}
                      textVariant={TEXT_VARIANTS.T20}
                    />
                  )}
                  renderMenu={(toggleOpen) => (
                    <Column
                      gap={5}
                      css={css`
                        width: 250px;
                        padding: 5px;
                      `}
                    >
                      <Row
                        crossAxisAlignment="center"
                        css={css`
                          height: 35px;
                          padding: 0 10px;
                        `}
                      >
                        <Text
                          variant={TEXT_VARIANTS.T15CAPS}
                          css={css`
                            color: ${COLORS.SILVER};
                          `}
                        >
                          {"SORT FEED BY"}
                        </Text>
                      </Row>
                      {Object.values(SORT_CRITERIA).map((criteria) => (
                        <ListItem
                          label={criteria}
                          active={criteria === sortCriteria}
                          onClick={() => {
                            setSortCriteria(criteria);
                            toggleOpen();
                          }}
                        />
                      ))}
                    </Column>
                  )}
                />
              </Row>
            )}
            <Feed />
          </Column>
        </Flex>
      </Column>
    </ProfileContext.Provider>
  );
};

export default Note;
